import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, Button, ComboBox, ComboBoxItem, Heading, ProgressCircle, Text, Tooltip, TooltipTrigger } from "@react-spectrum/s2";
import { Add, Delete, UserAdd } from '../icons/s2';
import { toast } from '../../utils/toast';
import { colorForUser, initialsForUser } from '../../utils/avatar';
import { CCOrgAdminEntry, OrgUserEntry } from '../../types';
import { fetchCCOrgAdmins, addCCOrgAdmin, revokeCCOrgAdmin, fetchOrgUsers } from '../../services/api';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { SidePanelDialog } from '../common/SidePanelDialog';
import { OrgUsersTable, OrgUsersTableHandle } from '../ManageUsers/OrgUsersTable';
import '../ManageUsers/ManageUsersPanel.css';
export interface OrgAdminsPanelProps {
    orgId: string;
}
/**
 * "Org Admins" tab of the Command Center Org Configuration page. Backed by
 * GET/POST /command-center/organizations/{org_id}/admins + DELETE
 * .../admins/{user_id}, which enforce at most one org_admin per org (POST
 * 409s if a different admin already exists) -- so the UI treats org_admin
 * as a single, replaceable seat rather than a list.
 *
 * The users list below is the same server-paginated/filterable "All users"
 * browser as Security > Users (OrgUsersTable), including the current admin,
 * with a single "Assign org admin" action (disabled for the current admin's
 * own row) in place of edit/delete.
 */ export const OrgAdminsPanel: React.FC<OrgAdminsPanelProps> = ({ orgId })=>{
    const { t } = useTranslation();
    const [admins, setAdmins] = useState<CCOrgAdminEntry[]>([]);
    const [loadingAdmin, setLoadingAdmin] = useState(true);
    const [assigningUserId, setAssigningUserId] = useState<string | null>(null);
    const [revokingUserId, setRevokingUserId] = useState<string | null>(null);
    const [pendingRevokeUserId, setPendingRevokeUserId] = useState<string | null>(null);
    const [pendingGrantUserId, setPendingGrantUserId] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newAdminId, setNewAdminId] = useState('');
    const [newAdminSuggestions, setNewAdminSuggestions] = useState<OrgUserEntry[]>([]);
    const usersTableRef = useRef<OrgUsersTableHandle>(null);
    const currentAdmin = admins[0] ?? null;
    const loadAdmins = useCallback(async ()=>{
        setLoadingAdmin(true);
        try {
            const result = await fetchCCOrgAdmins(orgId);
            setAdmins(result.admins);
        } catch (err) {
            toast.negative(err instanceof Error ? err.message : t('commandCenter.orgAdmins.failedToLoad'));
        } finally{
            setLoadingAdmin(false);
        }
    }, [
        orgId,
        t
    ]);
    useEffect(()=>{
        loadAdmins();
    }, [
        loadAdmins
    ]);
    const closeAddForm = useCallback(()=>{
        setNewAdminId('');
        setShowAddForm(false);
    }, []);
    // Server-side typeahead for the "Add user" ComboBox: search org members
    // (excluding the current admin) as the admin types, rather than filtering
    // an already-fetched full list on the client.
    useEffect(()=>{
        if (!showAddForm) return undefined;
        let cancelled = false;
        const timer = setTimeout(async ()=>{
            try {
                const result = await fetchOrgUsers(orgId, {
                    search: newAdminId.trim() || undefined,
                    excludeUserId: currentAdmin?.user_id,
                    pageSize: 8
                });
                if (!cancelled) setNewAdminSuggestions(result.users);
            } catch  {
                if (!cancelled) setNewAdminSuggestions([]);
            }
        }, 250);
        return ()=>{
            cancelled = true;
            clearTimeout(timer);
        };
    }, [
        showAddForm,
        newAdminId,
        orgId,
        currentAdmin
    ]);
    const handleAssign = useCallback(async (userId: string)=>{
        setAssigningUserId(userId);
        try {
            const result = await addCCOrgAdmin(orgId, userId);
            setAdmins(result.admins);
            toast.positive(t('commandCenter.orgAdmins.nowAdmin', {
                userId
            }));
            closeAddForm();
            usersTableRef.current?.reload();
        } catch (err) {
            // The backend rejects outright (409) when an org admin already
            // exists -- surface that message rather than silently revoking the
            // current admin on the caller's behalf. Removing the current admin
            // is a separate, explicit action (the "Remove org admin" button).
            toast.negative(err instanceof Error ? err.message : t('commandCenter.orgAdmins.failedToAssign'));
        } finally{
            setAssigningUserId(null);
        }
    }, [
        orgId,
        closeAddForm,
        t
    ]);
    // The table row action only *stages* a grant; promotion happens after the
    // admin explicitly confirms in the dialog, so a stray click can't silently
    // elevate a user to Super Admin. The payload/guards are unchanged -- this
    // just reuses the existing handleAssign path once confirmed.
    const handleGrantConfirm = useCallback(()=>{
        if (!pendingGrantUserId) return;
        const userId = pendingGrantUserId;
        setPendingGrantUserId(null);
        handleAssign(userId);
    }, [
        pendingGrantUserId,
        handleAssign
    ]);
    const handleRevoke = useCallback(async ()=>{
        if (!pendingRevokeUserId) return;
        const userId = pendingRevokeUserId;
        setPendingRevokeUserId(null);
        setRevokingUserId(userId);
        try {
            const result = await revokeCCOrgAdmin(orgId, userId);
            setAdmins(result.admins);
            toast.positive(t('commandCenter.orgAdmins.accessRevoked', {
                userId
            }));
            usersTableRef.current?.reload();
        } catch (err) {
            toast.negative(err instanceof Error ? err.message : t('commandCenter.orgAdmins.failedToRevoke'));
        } finally{
            setRevokingUserId(null);
        }
    }, [
        orgId,
        pendingRevokeUserId,
        t
    ]);
    return (<div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0
    }}>
      <div className={" Gs171"}>
        <Heading level={3} styles={" Gs171"}>{t('commandCenter.orgAdmins.heading')}</Heading>
        <span className="manage-users-subtitle">
          {t('commandCenter.orgAdmins.subtitle')}
        </span>
      </div>

      <div className={" JA171 GG171"}>
        <span className="manage-users-permissions-heading">{t('commandCenter.orgAdmins.currentAdmin')}</span>
        {loadingAdmin ? (<div className={" sd171 _Ca171 JC171"}>
            <ProgressCircle aria-label={t('commandCenter.orgAdmins.loadingAdmin')} isIndeterminate/>
          </div>) : currentAdmin ? (<div className="manage-users-table-shell" style={{
        padding: '14px 18px'
    }}>
            <div className={" sd171 _Ce171 eb171"}>
              <div className={" sd171 Ug171 qg171 eb171"}>
                <div className="manage-users-avatar" style={{
        background: colorForUser(currentAdmin.user_id)
    }} aria-hidden="true">
                  {initialsForUser(currentAdmin.display_name, currentAdmin.user_id)}
                </div>
                <div>
                  <div className="manage-users-user-id manage-users-primary-label">
                    {currentAdmin.display_name && currentAdmin.display_name !== currentAdmin.user_id ? currentAdmin.display_name : currentAdmin.user_id}
                  </div>
                  {currentAdmin.display_name && currentAdmin.display_name !== currentAdmin.user_id && (<span className="manage-users-audit-cell">{currentAdmin.user_id}</span>)}
                </div>
              </div>
              {revokingUserId === currentAdmin.user_id ? (<ProgressCircle aria-label={t('commandCenter.orgAdmins.removing')} isIndeterminate size="S"/>) : (<TooltipTrigger delay={300}>
                  <ActionButton isQuiet onPress={()=>setPendingRevokeUserId(currentAdmin.user_id)} aria-label={t('commandCenter.orgAdmins.removeOrgAdmin')}>
                    <Delete size="S"/>
                  </ActionButton>
                  <Tooltip>{t('commandCenter.orgAdmins.removeOrgAdmin')}</Tooltip>
                </TooltipTrigger>)}
            </div>
          </div>) : (<div className="manage-users-empty-state manage-users-empty-state--dashed">
            <span>{t('commandCenter.orgAdmins.noAdmin')}</span>
          </div>)}
      </div>

      {!loadingAdmin && (<OrgUsersTable ref={usersTableRef} orgId={orgId} pageSize={5} toolbarEnd={(<ActionButton onPress={()=>setShowAddForm(true)}>
              <Add/>
              <Text>{t('commandCenter.orgAdmins.addUser')}</Text>
            </ActionButton>)} renderActions={(user)=>{
        const isCurrentAdmin = currentAdmin?.user_id === user.user_id;
        return assigningUserId === user.user_id ? (<ProgressCircle aria-label={t('commandCenter.orgAdmins.assigning')} isIndeterminate size="S"/>) : (<TooltipTrigger delay={300}>
                <ActionButton isQuiet isDisabled={isCurrentAdmin} onPress={()=>setPendingGrantUserId(user.user_id)} aria-label={isCurrentAdmin ? t('commandCenter.orgAdmins.alreadySuperAdminAria', {
            userId: user.user_id
        }) : t('commandCenter.orgAdmins.grantSuperAdminAria', {
            userId: user.user_id
        })}>
                  <UserAdd size="S"/>
                </ActionButton>
                <Tooltip>{isCurrentAdmin ? t('commandCenter.orgAdmins.alreadySuperAdmin') : t('commandCenter.orgAdmins.grantSuperAdminRole')}</Tooltip>
              </TooltipTrigger>);
    }}/>)}

      <SidePanelDialog isOpen={showAddForm} onDismiss={closeAddForm} title={t('commandCenter.orgAdmins.addUser')} ariaLabel={t('commandCenter.orgAdmins.addUser')} footer={(<>
            <Button variant="accent" onPress={()=>handleAssign(newAdminId.trim())} isPending={assigningUserId === newAdminId.trim() && !!newAdminId.trim()} isDisabled={!newAdminId.trim()}>
              {t('commandCenter.orgAdmins.assignOrgAdmin')}
            </Button>
            <Button variant="secondary" onPress={closeAddForm} isDisabled={!!assigningUserId}>
              {t('primitives.cancel')}
            </Button>
          </>)}>
        <div className={" sd171 _ta171 Ul171 ql171"}>
          <ComboBox label={t('commandCenter.orgAdmins.userLabel')} allowsCustomValue inputValue={newAdminId} onInputChange={setNewAdminId} onSelectionChange={(key)=>{
        if (key !== null) setNewAdminId(String(key));
    }} styles={" Za171"} isDisabled={assigningUserId === newAdminId.trim() && !!newAdminId.trim()} description={t('commandCenter.orgAdmins.userDescription')} autoFocus>
            {newAdminSuggestions.map((u)=>(<ComboBoxItem key={u.user_id} id={u.user_id} textValue={u.user_id}>
                {u.display_name && u.display_name !== u.user_id ? <Text slot="label">{t('commandCenter.orgAdmins.userOption', {
            name: u.display_name,
            id: u.user_id
        })}</Text> : <Text slot="label">{u.user_id}</Text>}
              </ComboBoxItem>))}
          </ComboBox>
        </div>
      </SidePanelDialog>

      <ConfirmDialog isOpen={!!pendingRevokeUserId} onOpenChange={(open)=>{
        if (!open) setPendingRevokeUserId(null);
    }} title={t('commandCenter.orgAdmins.removeOrgAdmin')} confirmLabel={t('commandCenter.orgAdmins.remove')} variant="destructive" onConfirm={handleRevoke}>
        {t('commandCenter.orgAdmins.revokeConfirm', {
        userId: pendingRevokeUserId
    })}
      </ConfirmDialog>

      <ConfirmDialog isOpen={!!pendingGrantUserId} onOpenChange={(open)=>{
        if (!open) setPendingGrantUserId(null);
    }} title={t('commandCenter.orgAdmins.grantSuperAdmin')} confirmLabel={t('commandCenter.orgAdmins.grantSuperAdmin')} cancelLabel={t('primitives.cancel')} variant="confirmation" onConfirm={handleGrantConfirm}>
        {t('commandCenter.orgAdmins.grantConfirm', {
        userId: pendingGrantUserId
    })}
      </ConfirmDialog>
    </div>);
};
export default OrgAdminsPanel;

import "macro-2a47a5f41d025ffa8390da34da1f7672ff9c7f9263c2e7820df3daefe6c31ffd.css";
import "macro-2a47a5f41d025ffa8390da34da1f7672ff9c7f9263c2e7820df3daefe6c31ffd.css";
import "macro-0c93f61949087b6b5b8301aae6e8b64eb981dcf7206050ea25ac65ede32d4ae2.css";
import "macro-977d3d44da9cbcdbed87d7e6f1b5b54943965f252e8d93bd1a8b6fe5290c038b.css";
import "macro-7c96c60cc14f2810ab252b866e87014b1715727505ea9960f896394a53565bec.css";
import "macro-c8192579c3fcf9ce28f716914ac6aee5c478a7952189343876b9a00274001063.css";
import "macro-e77d45646f8bdb461ee9552438da3de2016a7864660e2aa4864c36c173c39db6.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";